import Button, { type ButtonProps } from "@client/controls/Button/component";
import { useDidUpdate } from "@client/lib/hooks";
import { EMPTY_OBJECT } from "@client/lib/misc";
import { generateId } from "@client/lib/random";
import { type FormLayout, type GenericMap, type I18n } from "@client/types";
import MediaUploaderList from "@common/components/Media/Upload/List/component";
import { type UploadEndpointInfo } from "@common/components/Media/Upload/types";
import {
  type FormHandles as CommonFormHandles,
  FormButtons,
  FormControlled,
  type FormControlledRenderParams,
  FormGroup,
  type Validation
} from "@common/controls/Form/component";
import { mapFormElements } from "@common/controls/Form/DynamicForm/lib";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import { type FormFieldValidateOn } from "@common/controls/Form/Field/component";
import { type UploadedFile } from "@common/controls/Upload/types";
import { useI18n } from "@common/hooks/i18n";
import { identity } from "lodash";
import * as React from "react";
import ClientFormField, { type FormFieldComponent } from "./Field/component";
import "./style.less";

export interface ControlFormField {
  addon?: any;
  colSpan?: number | null;
  Component: FormFieldComponent;
  componentAcceptsLabel?: boolean;
  componentOpts?: any;
  defaultValue?: any;
  dependencies?: string[];
  description?: string;
  error?: string | null;
  help?: string;
  isDisabled?: boolean;
  isNoStyle?: boolean;
  isPaddingOnContainer?: boolean;
  isRequired?: boolean;
  label: string;
  name: string;
  validation?: Validation;
  width?: string;
  qa?: string;
}

export interface SeparatorFormField {}

export type FormFieldInfo = ControlFormField | SeparatorFormField;

export interface FormButtonProps {
  ifValidated: (onSubmit: (data: FormData) => void | Promise<any>) => (e: React.MouseEvent) => void;
  isDisabled?: boolean;
}

export interface WithAttachments {
  attachments: UploadedFile[];
}

export interface FormProps<TButtonProps = ButtonProps> {
  additionalFields?: FormFieldInfo[] | DynamicForms.Field[];
  additionalFieldsPlacement?: "aboveButton" | "belowButton";
  additionalFieldsPrepend?: any;
  className?: string;
  fields: FormFieldInfo[];
  isAdminColor?: boolean;
  layout?: FormLayout;
  showStars?: boolean;
  isInline?: boolean;
  error?: string | null;
  i18n?: I18n;
  resetOnSubmit?: boolean;
  style?: React.CSSProperties;
  invisibleIfEmpty?: boolean;

  onChange?: (name: string, value: any) => void;

  bottomHint?: string | null;
  buttonsStyle?: React.CSSProperties;

  buttonProps?: TButtonProps;
  Button?: (props: FormButtonProps) => any;
  extraButtons?: (props: FormButtonProps) => any;
  onSubmit?: (data: FormData & Partial<WithAttachments>) => void | Promise<any>;

  validateOn?: FormFieldValidateOn;
  uploadApiInfo?: UploadEndpointInfo | null;
  topInfo?: string | React.ReactElement | null;
  bottomInfo?: string | React.ReactElement | null;
  /** IMPORTANT it's workaround, need refactoring, don't use it */
  values?: GenericMap<any>;
}

export type FormData = GenericMap<any>;

export type FormHandles = {
  validate: () => Promise<string | null>;
  getFinalValues: () => Promise<any | null>;
  ifValidated: (onSubmit: (data: FormData) => void | Promise<any>) => (e: React.MouseEvent) => Promise<void>;
};

const Form = React.forwardRef(({ resetOnSubmit, ...props }: FormProps, ref: React.RefObject<FormHandles>) => {
  const i18n = useI18n();

  const [isUploading, setIsUploading] = React.useState(false);
  const [values, setValues] = React.useState<GenericMap<any>>(props.values ?? {});

  useDidUpdate(() => setValues(props.values ?? {}), [props.values]);

  const defaultValues: GenericMap<any> = React.useMemo(
    () =>
      [...props.fields, ...(props.additionalFields ?? [])]
        .filter((f) => "name" in f)
        .reduce(
          (res, f: ControlFormField | DynamicForms.Field) => {
            return {
              ...res,
              [f.name]: values[f.name] ?? (f as ControlFormField).defaultValue ?? (f as DynamicForms.Field).value
            };
          },
          {
            mediaIds: values.mediaIds
          }
        ),
    [props.fields, values]
  );

  const onChangeInner = React.useCallback(
    (name: string, value: any) => {
      setValues((x) => ({ ...x, [name]: value }));
      props.onChange && props.onChange(name, value);
    },
    [props.onChange]
  );

  const formRef = React.useRef<CommonFormHandles>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        const validateResult = formRef.current?.validate();
        if (validateResult instanceof Promise) {
          const r = await validateResult;
          return r ? null : i18n.invalid;
        }
        return validateResult ? null : i18n.invalid;
      },
      getFinalValues: async () => {
        return await formRef.current?.getFinalValues();
      },
      ifValidated: ifValidated
    }),
    [formRef.current]
  );

  const renderFields = React.useCallback(
    (fieldsForRender: FormFieldInfo[], values: any[], onChange: (name: string, value: any) => any) => {
      const elements: React.ReactElement[] = [];

      // TODO: refactor
      for (
        let i = 0, fi = 0, rowIndex = -1, colSpan = 0, row: React.ReactElement[] = [], isHidden = true;
        i < fieldsForRender.length;
        i++
      ) {
        const f = fieldsForRender[i];

        if ("Component" in f) {
          const dependencies = f.dependencies
            ? f.dependencies.reduce((res, d) => ({ ...res, [d]: values[d] }), {})
            : EMPTY_OBJECT;
          const fieldColSpan = props.layout?.stackFields
            ? 12
            : f.componentOpts?.type === "hidden"
            ? 0
            : f.colSpan || 12;

          isHidden = isHidden && f.componentOpts?.type === "hidden";

          row.push(
            <ClientFormField
              key={f.name}
              className=""
              colSpan={fieldColSpan}
              Component={f.Component}
              componentOpts={{
                ...f.componentOpts,
                isRequired: f.isRequired,
                name: f.name,
                dependencies: f.componentOpts?.dependencies ?? dependencies
              }}
              error={f.error}
              help={f.help}
              hint={f.description}
              isPaddingOnContainer={f.isPaddingOnContainer}
              isDisabled={f.isDisabled}
              isNoStyle={f.isNoStyle}
              isRequired={f.isRequired}
              label={f.label}
              name={f.name}
              onChange={onChange}
              qa={f.qa}
              showStar={props.showStars}
              validateOn={props.validateOn}
              validation={f.validation}
              value={values[f.name]}
              width={f.width}
            />
          );

          fi++;
          colSpan += fieldColSpan;
        }

        const isSeparator = !("Component" in f);
        if (colSpan >= 12 || isSeparator || i === fieldsForRender.length - 1) {
          elements.push(<FormGroup key={`group-${rowIndex}`}>{row}</FormGroup>);
          row = [];
          rowIndex++;

          isHidden = true;
          colSpan = colSpan % 12;
        }

        if (isSeparator) {
          elements.push(<hr key={`separator-${i}`} />);
          colSpan = 0;
        }
      }

      return elements;
    },
    [props.layout?.stackFields, props.showStars, props.isAdminColor, props.validateOn]
  );

  const ifValidated = React.useCallback(
    (onSubmit: (data: FormData) => void | Promise<any>) => async (e: React.MouseEvent) => {
      e.preventDefault();
      const isValid = await formRef.current!.validate();
      if (!isValid) return;

      const finalValues = await formRef.current!.getFinalValues();
      const submitResult = onSubmit({ ...finalValues, mediaIds: values.mediaIds });
      if (submitResult instanceof Promise) {
        await submitResult;
      }
      if (resetOnSubmit) {
        setValues({});
        setResetMedia(generateId());
      }
    },
    [resetOnSubmit, values]
  );

  const [resetMedia, setResetMedia] = React.useState<string>();

  // TODO: we shouldn't pass identity to submit, as it blocks form submission on Enter press
  return (
    <FormControlled ref={formRef} defaultValues={defaultValues} {...props} onChange={onChangeInner} submit={identity}>
      {({ values, onChange, isSubmitting, submit }: FormControlledRenderParams<any>) => {
        const renderedAdditionalFields = props.additionalFields?.length
          ? (props.additionalFields[0] as ControlFormField).Component
            ? renderFields(props.additionalFields, values, onChange)
            : mapFormElements(i18n, props.additionalFields as DynamicForms.Field[], values, { onChange })
          : [];
        return (
          <>
            {!!props.topInfo && props.topInfo}
            {renderFields(props.fields, values, onChange)}
            {!!props.bottomInfo && props.bottomInfo}

            {props.uploadApiInfo && (
              <MediaUploaderList
                key={resetMedia}
                maxSizeMb={props.uploadApiInfo.maxSizeMb}
                {...props.uploadApiInfo}
                mode="link"
                onChange={(x) => {
                  onChange("mediaIds", x);
                  setIsUploading(false);
                }}
                onStartUploading={() => setIsUploading(true)}
                value={values.mediaIds}
              />
            )}

            {!!props.bottomHint && <div className="hint">{props.bottomHint}</div>}

            {props.additionalFieldsPlacement === "aboveButton" ? props.additionalFieldsPrepend : null}
            {renderedAdditionalFields.length && props.additionalFieldsPlacement === "aboveButton"
              ? renderedAdditionalFields
              : null}

            <FormButtons style={props.buttonsStyle}>
              {props.Button ? (
                <props.Button
                  {...(props.buttonProps || {})}
                  isDisabled={isSubmitting || isUploading}
                  ifValidated={ifValidated}
                />
              ) : props.onSubmit ? (
                <Button className="btn-submit" isDisabled={isSubmitting || isUploading} onClick={submit}>
                  Submit
                </Button>
              ) : null}
              {props.extraButtons && (
                <props.extraButtons isDisabled={isSubmitting || isUploading} ifValidated={ifValidated} />
              )}
            </FormButtons>
            {props.additionalFieldsPlacement === "belowButton" ? props.additionalFieldsPrepend : null}
            {renderedAdditionalFields.length && props.additionalFieldsPlacement === "belowButton"
              ? renderedAdditionalFields
              : null}

            {props.error && <div className="error_box">{props.error}</div>}
          </>
        );
      }}
    </FormControlled>
  );
});

export default Form;
